import { useRef, useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal, RolePicker } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { JobDescriptionDoc, JobDescriptionView, Me, PeopleTemplate, Role } from '../../lib/types';

/**
 * The job description library — the signed document for each role.
 *
 * Distinct from the free-text "job description" on a People Template: that one
 * is the summary that appears in a review, this is the document somebody
 * actually signed.
 *
 * The files live in the database rather than on disk — see migration 032. That
 * also means a document cannot be linked to directly: it is served behind the
 * same bearer token as everything else, so it is fetched and handed to the
 * browser as an object URL.
 */

const MAX_MB = 10;
const ACCEPT = '.pdf,.doc,.docx,.txt,.md,.rtf';

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const extensionOf = (name: string) => (name.split('.').pop() ?? '').toUpperCase();

/** Reads a file as base64, without the `data:...;base64,` prefix the API does
 *  not want. FileReader is the only way to do this that streams rather than
 *  materialising the whole file as a JS string twice. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read "${file.name}".`));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

/** Opens a stored document in a new tab, or saves it. */
async function openDocument(doc: JobDescriptionDoc, download: boolean): Promise<void> {
  const blob = await api.getBlob(
    `/people/job-descriptions/${doc.JobDescriptionId}/file${download ? '?download=1' : ''}`,
  );
  const url = URL.createObjectURL(blob);
  if (download) {
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.FileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  /* Revoked on a delay rather than immediately — Safari cancels the load if
     the object URL disappears before it has read it. */
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function JobDescriptions({ onChanged }: { onChanged?: () => void }) {
  const docs = useResource<JobDescriptionDoc[]>('/people/job-descriptions');
  /* Read here rather than threaded down from App: this is the only thing on
     the People tabs that cares who is looking, and the API is the real gate —
     hiding the button is presentation, requireAdmin on the route is the rule. */
  const me = useResource<Me>('/me');
  const isAdmin = me.data?.appRole === 'admin';
  const templates = useResource<PeopleTemplate[]>('/people/templates');
  const roles = useResource<Role[]>('/roles');
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<JobDescriptionDoc | null>(null);
  const [reading, setReading] = useState<JobDescriptionDoc | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    docs.reload();
    onChanged?.();
  };

  if (docs.loading) return <Loading label="Loading the library" />;
  if (docs.error) return <ErrorBanner message={docs.error} />;

  const all = docs.data ?? [];
  const q = search.trim().toLowerCase();
  const visible = q
    ? all.filter((d) =>
        [d.Title, d.RoleName, d.FileName, d.TemplateName, d.Notes].some((v) =>
          (v ?? '').toLowerCase().includes(q),
        ),
      )
    : all;

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="muted small" style={{ maxWidth: '60ch' }}>
          One job description document per role, optionally linked to its People Template. PDF,
          Word, RTF, text or Markdown, up to {MAX_MB} MB.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setUploading(true)}>
          Upload
        </button>
      </div>

      {error && <ErrorBanner message={error} />}

      {all.length > 0 && (
        <input
          className="input mb-4"
          style={{ maxWidth: 360 }}
          placeholder="Search titles, roles, filenames and notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}

      {all.length === 0 ? (
        <Empty title="No job descriptions yet">
          <p className="muted">Upload the signed document for a role to start the library.</p>
        </Empty>
      ) : visible.length === 0 ? (
        <Empty title={`Nothing matches “${search}”`} />
      ) : (
        <div className="grid grid-3">
          {visible.map((d) => (
            <section key={d.JobDescriptionId} className="card">
              <div className="card-head">
                <div className="grow row">
                  <span className="doc-ext">{extensionOf(d.FileName)}</span>
                  <div>
                    <h3>{d.Title}</h3>
                    {d.RoleName && <div className="sub">{d.RoleName}</div>}
                  </div>
                </div>
              </div>
              <div className="card-body">
                {d.Notes && <p className="small muted">{d.Notes}</p>}
                {d.TemplateName && <p className="xs subtle mt-2">Template: {d.TemplateName}</p>}
                <p className="xs subtle mt-2" title={d.FileName}>
                  {humanSize(d.SizeBytes)} · uploaded {formatDate(d.UploadedUtc)}
                </p>
                <div className="row wrap mt-4">
                  {/* Reading comes first and stays in the app. Before this the
                      only two buttons both downloaded the file, because a .docx
                      is served as an attachment — so "look at the job
                      description" meant opening Word. */}
                  {d.HasHtml ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setReading(d)}
                    >
                      Read
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        void openDocument(d, false).catch((err: unknown) =>
                          setError(err instanceof Error ? err.message : 'Could not open the document'),
                        );
                      }}
                    >
                      Open file
                    </button>
                  )}
                  {/* Admin only, and the API refuses it for anybody else — this
                      is the copy somebody edits in Word and re-uploads, and
                      handing it out widely is how a second master appears. */}
                  {isAdmin && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        void openDocument(d, true).catch((err: unknown) =>
                          setError(err instanceof Error ? err.message : 'Could not download'),
                        );
                      }}
                    >
                      Download master description
                    </button>
                  )}
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(d)}>
                    Edit
                  </button>
                  <DeleteButton
                    what={`"${d.Title}"? The uploaded file goes with it`}
                    onConfirm={() => {
                      void api.del(`/people/job-descriptions/${d.JobDescriptionId}`).then(reload);
                    }}
                  />
                </div>
              </div>
            </section>
          ))}
        </div>
      )}

      {uploading && (
        <UploadDialog
          templates={templates.data ?? []}
          roles={roles.data ?? []}
          onClose={() => setUploading(false)}
          onDone={() => {
            setUploading(false);
            reload();
          }}
        />
      )}


      {reading && (
        <DocumentReader doc={reading} onClose={() => setReading(null)} />
      )}

      {editing && (
        <EditDialog
          doc={editing}
          templates={templates.data ?? []}
          roles={roles.data ?? []}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function UploadDialog({
  templates,
  roles,
  onClose,
  onDone,
}: {
  templates: PeopleTemplate[];
  roles: Role[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [roleId, setRoleId] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const choose = (chosen: File | null | undefined) => {
    if (!chosen) return;
    setError(null);
    if (chosen.size > MAX_MB * 1024 * 1024) {
      setError(`"${chosen.name}" is ${humanSize(chosen.size)} — the limit is ${MAX_MB} MB.`);
      return;
    }
    setFile(chosen);
    /* Seed the title from the filename so the common case needs no typing. */
    setTitle((current) => current || chosen.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async () => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      await api.post('/people/job-descriptions', {
        title: title.trim(),
        roleId,
        templateId: templateId ? Number(templateId) : null,
        notes: notes.trim() || null,
        fileName: file.name,
        content: await toBase64(file),
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Upload a job description"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !file}
            onClick={() => void submit()}
          >
            {saving ? 'Uploading…' : 'Upload'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="field">
        <label>Document</label>
        <input
          ref={inputRef}
          className="input"
          type="file"
          accept={ACCEPT}
          onChange={(e) => choose(e.target.files?.[0])}
        />
        <span className="xs subtle">
          {file ? `${file.name} · ${humanSize(file.size)}` : `PDF, Word, RTF, text or Markdown · up to ${MAX_MB} MB`}
        </span>
      </div>

      <Field label="Title">
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Help Desk Technician — job description"
        />
      </Field>

      <div className="form-grid">
        <Field label="Role" hint="From the directory. Add a missing one on the Employees page.">
          <RolePicker roles={roles} value={roleId} onChange={setRoleId} />
        </Field>
        <Field label="People Template">
          <select
            className="select"
            value={templateId}
            onChange={(e) => {
              const id = e.target.value;
              setTemplateId(id);
              /* Picking a template offers its role when none is chosen yet.
                 It offers the template's LINKED role rather than matching on a
                 name — there is no name to match on any more. */
              const t = templates.find((x) => String(x.TemplateId) === id);
              if (t?.RoleId) setRoleId((current) => current ?? t.RoleId);
            }}
          >
            <option value="">Not linked</option>
            {templates.map((t) => (
              <option key={t.TemplateId} value={t.TemplateId}>
                {t.Name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Notes" hint="Version, approval date, anything worth remembering.">
        <textarea className="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}

function EditDialog({
  doc,
  templates,
  roles,
  onClose,
  onDone,
}: {
  doc: JobDescriptionDoc;
  templates: PeopleTemplate[];
  roles: Role[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(doc.Title);
  const [roleId, setRoleId] = useState<number | null>(doc.RoleId);
  const [templateId, setTemplateId] = useState(doc.TemplateId ? String(doc.TemplateId) : '');
  const [notes, setNotes] = useState(doc.Notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/people/job-descriptions/${doc.JobDescriptionId}`, {
        title: title.trim(),
        roleId,
        templateId: templateId ? Number(templateId) : null,
        notes: notes.trim() || null,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit job description"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <p className="xs subtle">
        {doc.FileName} · {humanSize(doc.SizeBytes)}. Replacing the file means uploading a new
        document — the record is the document, not a wrapper round it.
      </p>
      <Field label="Title">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div className="form-grid">
        <Field label="Role">
          <RolePicker roles={roles} value={roleId} onChange={setRoleId} />
        </Field>
        <Field label="People Template">
          <select className="select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Not linked</option>
            {templates.map((t) => (
              <option key={t.TemplateId} value={t.TemplateId}>
                {t.Name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Notes">
        <textarea className="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}

/**
 * The document, read in the app.
 *
 * The HTML is derived from the uploaded .docx and is never the master — see
 * migration 103. Somebody who needs to CHANGE it downloads the file, edits it
 * in Word and uploads it again, which is why that button is admin-only and
 * this one is not.
 *
 * Rendered with dangerouslySetInnerHTML, on the same footing as the operations
 * manual's own markup: it comes from mammoth, whose output is a small fixed
 * set of tags — headings, lists, emphasis, tables, data: images — with no
 * script among them, and it is produced by this app's own server from a file
 * an administrator uploaded.
 */
function DocumentReader({ doc, onClose }: { doc: JobDescriptionDoc; onClose: () => void }) {
  const view = useResource<JobDescriptionView>(
    `/people/job-descriptions/${doc.JobDescriptionId}/view`,
  );

  return (
    <Modal
      title={doc.Title}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      {view.loading && <Loading label="Reading the document" />}
      {view.error && <ErrorBanner message={view.error} />}
      {view.data &&
        (view.data.Html ? (
          <>
            <p className="xs subtle mb-4">
              Read-only, converted from {view.data.FileName}. The Word file is the master.
            </p>
            {/* Its own document styling rather than the manual's prose styling:
                this is a signed document somebody wrote in Word, and it should
                read like one — a page, a title block, ruled tables. */}
            <div
              className="jd-doc"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: view.data.Html }}
            />
          </>
        ) : (
          <Empty title="No readable version">
            This file could not be converted — it is a PDF, an older .doc, or a document the
            converter could not read. The file itself is unaffected.
          </Empty>
        ))}
    </Modal>
  );
}
